import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  Dispatch,
  SetStateAction,
} from "react";
import dayjs, { Dayjs } from "dayjs";

interface DateRangeContextType {
  rangeTimeValue: [Dayjs, Dayjs];
  setRangeTimeValue: Dispatch<SetStateAction<[Dayjs, Dayjs]>>;
}

interface DateRangeHookType {
  rangeTimeValue: [Dayjs, Dayjs];
  setRangeTimeValue: Dispatch<SetStateAction<[Dayjs, Dayjs]>>;
  handleMonthChange: (date: dayjs.Dayjs | null) => void;
  handleRangeChange: (
    dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null
  ) => void;
  /**
   * Formatea las fechas del rango para uso en APIs
   * @returns Objeto con fechas formateadas: { startDate: string, endDate: string }
   */
  getFormattedDates: () => { startDate: string; endDate: string };
}

const DateRangeContext = createContext<DateRangeContextType | undefined>(
  undefined
);

interface DateRangeProviderProps {
  children: ReactNode;
}

/**
 * Provider que maneja el estado global del rango de fechas seleccionado
 * @param children - Componentes hijos que tendrán acceso al contexto
 */
export const DateRangeProvider = ({ children }: DateRangeProviderProps) => {
  const [rangeTimeValue, setRangeTimeValue] = useState<[Dayjs, Dayjs]>(() => {
    const storedValue = localStorage.getItem("dateRange");
    if (storedValue) {
      try {
        const parsed = JSON.parse(storedValue);
        // Convertir las fechas string de vuelta a objeto Dayjs
        return [dayjs(parsed[0]), dayjs(parsed[1])];
      } catch (error) {
        console.error("Error parsing stored date range:", error);
      }
    }
    // Valor por defecto si no hay nada en localStorage
    return [dayjs().startOf("month"), dayjs().endOf("month")];
  });

  useEffect(() => {
    // Guardar en localStorage cuando cambie el rangeTimeValue
    // Convertir Dayjs a string para almacenamiento
    const serializedValue = [
      rangeTimeValue[0].toISOString(),
      rangeTimeValue[1].toISOString(),
    ];
    localStorage.setItem("dateRange", JSON.stringify(serializedValue));
  }, [rangeTimeValue]);

  return (
    <DateRangeContext.Provider
      value={{
        rangeTimeValue,
        setRangeTimeValue,
      }}
    >
      {children}
    </DateRangeContext.Provider>
  );
};

/**
 * Hook personalizado para acceder y manipular el rango de fechas
 * @returns Objeto con el estado del rango y funciones para manipularlo
 * @throws Error si se usa fuera del DateRangeProvider
 */
export const useDateRange = (): DateRangeHookType => {
  const context = useContext(DateRangeContext);
  if (!context) {
    throw new Error("useDateRange debe usarse dentro de un DateRangeProvider");
  }
  const { rangeTimeValue, setRangeTimeValue } = context;

  /**
   * Maneja el cambio de fecha en un DatePicker individual (modo mes)
   * Establece el rango desde el primer día hasta el último día del mes seleccionado
   * @param date - Fecha seleccionada o null si se limpia
   */
  function handleMonthChange(date: dayjs.Dayjs | null) {
    if (date) {
      const newRange: [dayjs.Dayjs, dayjs.Dayjs] = [
        date.startOf("month"), // Primer día del mes a las 00:00:00
        date.endOf("month"), // Último día del mes a las 23:59:59
      ];
      setRangeTimeValue(newRange);
    }
  }

  /**
   * Maneja el cambio de fechas en un RangePicker (modo período)
   * Establece el rango exacto seleccionado por el usuario
   * @param dates - Tuple con las dos fechas seleccionadas o null
   */
  function handleRangeChange(
    dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null
  ) {
    if (dates && dates[0] && dates[1]) {
      const adjustedRange: [Dayjs, Dayjs] = [
        dates[0].startOf("month"),
        dates[1].endOf("month"),
      ];
      setRangeTimeValue(adjustedRange);
    }
  }

  /**
   * Formatea las fechas del rango actual para uso en APIs
   * @returns Objeto con las fechas en formato 'YYYY-MM-DD HH:mm:ss'
   */
  const getFormattedDates = () => {
    return {
      startDate: rangeTimeValue[0].format("YYYY-MM-DD 00:00:00"),
      endDate: rangeTimeValue[1].format("YYYY-MM-DD 23:59:59"),
    };
  };

  return {
    ...context,
    handleMonthChange,
    handleRangeChange,
    getFormattedDates,
  };
};

export default DateRangeContext;
